import {
  OAuthResponse,
  Session,
  SignInWithOAuthCredentials,
  SignInWithPasswordCredentials,
  SignUpWithPasswordCredentials,
  User,
} from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { AccountType } from '@/api/enums/supabase';
import { PublicRoutes, PMPRoutes } from '@/config/routes';
import { useResidentLoginMutation } from '@/hooks/useResidentLoginMutation';
import { getPendingOAuthCampaign, clearPendingOAuthCampaign } from '@/localStorage/campaign';
import {
  clearSupabaseSessionFromLocalStorage,
  getSupabaseSessionFromLocalStorage,
  setSupabaseSessionInLocalStorage,
} from '@/localStorage/supabase';
import { supabase } from '@/utils/supabaseClient';

type AuthResponse = {
  data: {
    user: User | null;
    session: Session | null;
  };
  error: Error | null;
};

type AuthContextType = {
  refreshUser: () => Promise<void>;
  signInWithPassword: (credentials: SignInWithPasswordCredentials) => Promise<AuthResponse>;
  signInWithOAuth: (credentials: SignInWithOAuthCredentials) => Promise<OAuthResponse>;
  signInAnonymously: () => Promise<AuthResponse>;
  signUp: (credentials: SignUpWithPasswordCredentials) => Promise<AuthResponse>;
  signOut: () => Promise<void>;
  resetPasswordForEmail: (
    email: string,
    captchaToken?: string,
    redirectTo?: string
  ) => Promise<{ error: Error | null }>;
  user: User | null;
  session: Session | null;
  shouldHaveAuthToken: boolean;
  isAuthenticated: boolean;
  isPropertyManager: boolean;
  isResident: boolean;
  checkAndHandleReferral: () => Promise<string | null>;
  isAuthLoading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(getSupabaseSessionFromLocalStorage());
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const residentLoginMutation = useResidentLoginMutation(session);

  // Haing Auth Token means that user is most likely logged in to an existing account,
  // but we might not yet have fetched their user details, so we use hasAuthToken to keep
  // track for edge cases.
  const [hasAuthToken, setHasAuthToken] = useState<boolean>(!!session);

  // If we're being redirected from OAuth, we get access_token in the location hash,
  // but there's a tick delay before it gets processed and stored as Auth Token in Local Storage.
  // At the same time, on the next tick, the access_token hash will disappear.
  // But we normally set hasAuthToken after auth state changes, but setState has a tick delay,
  // so there'll be a single tick when the Auth Token is present, but it's not yet reflected in
  // React state.
  // To handle that we set the flag already to true.
  useEffect(() => {
    if (location.hash.includes('access_token')) setHasAuthToken(true);
  }, [location.hash]);

  const shouldHaveAuthToken = useMemo(
    () => hasAuthToken || location.hash.includes('access_token'),
    [hasAuthToken, location]
  );

  const user = useMemo(() => session?.user ?? null, [session]);

  const isAuthenticated = useMemo(() => !!user, [user]);

  // All users are property managers now
  const isPropertyManager = true;

  // No longer supporting resident accounts
  const isResident = false;

  const refreshUser = useCallback(async () => {
    const _user = (await supabase.auth.getUser()).data.user;

    if (!_user) {
      setSession(null);
      clearSupabaseSessionFromLocalStorage();
      return;
    }

    setSession(_session => {
      if (!_session) return _session;

      const newSession: Session = {
        ..._session,
        user: _user,
      };
      setSupabaseSessionInLocalStorage(newSession);

      return newSession;
    });
  }, []);

  useEffect(() => {
    // Check initial auth state
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (!currentSession && !session) {
        setIsAuthLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, _session) => {
      if (isEqual(session, _session)) {
        setIsAuthLoading(false);
        return;
      }

      setSession(_session);
      setHasAuthToken(!!_session);
      setIsAuthLoading(false);
      queryClient.clear();

      // Handle password recovery event
      if (_event === 'PASSWORD_RECOVERY') {
        history.push(PublicRoutes.resetPassword);
        return;
      }

      // Handle pending OAuth campaign for new signins and token refreshes
      if (_session?.user && (_event === 'SIGNED_IN' || _event === 'TOKEN_REFRESHED')) {
        // Check for share redirect URL first (for existing users)
        if (_event === 'SIGNED_IN') {
          // Check for vendor search state preservation
          const vendorSearchState = sessionStorage.getItem('vendorSearchState');
          if (vendorSearchState) {
            // User was in the middle of a vendor search, redirect to property manager portal
            history.push(PMPRoutes.vendorSearchResults);
            return;
          }

          const shareRedirectUrl = localStorage.getItem('shareRedirectUrl');
          if (shareRedirectUrl) {
            // Only redirect if it's a valid share URL, not a general property-manager route
            if (shareRedirectUrl.includes('/share/')) {
              localStorage.removeItem('shareRedirectUrl');

              // Check if this is a new user who might have pending referral data
              const pendingReferralStr = localStorage.getItem('pendingReferral');
              if (pendingReferralStr) {
                // New user with referral data - let the referral handler process it
                // Don't redirect immediately
              } else {
                // Existing user - redirect immediately
                history.push(shareRedirectUrl);
                return;
              }
            } else {
              // Clear invalid/stale redirect URLs
              localStorage.removeItem('shareRedirectUrl');
            }
          }
        }

        const pendingCampaign = getPendingOAuthCampaign();

        if (pendingCampaign && !_session.user.user_metadata?.campaign) {
          try {
            // Update user metadata with campaign
            await supabase.auth.updateUser({
              data: {
                ..._session.user.user_metadata,
                campaign: pendingCampaign,
              },
            });
            // Clean up localStorage after successful update
            clearPendingOAuthCampaign();
          } catch (error) {
            // Failed to update user metadata with campaign
          }
        } else if (pendingCampaign) {
          // Clean up if campaign already exists
          clearPendingOAuthCampaign();
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [session, queryClient, history]);

  const signInWithPassword = useCallback(
    async (credentials: SignInWithPasswordCredentials): Promise<AuthResponse> => {
      if (user?.is_anonymous) {
        await supabase.auth.updateUser(credentials);
      }

      const response = await supabase.auth.signInWithPassword(credentials);

      // Allow login even if email is not confirmed
      // Supabase will handle this based on its configuration
      // If you get email_not_confirmed error, it means Supabase still requires confirmation
      // You'll need to disable this in Supabase dashboard

      // If the user is a resident, update their onboarding state
      if (
        !response.error &&
        response.data.user?.user_metadata.account_type === AccountType.resident
      ) {
        await residentLoginMutation.mutateAsync();
      }

      return response;
    },
    [user, residentLoginMutation]
  );

  const signInWithOAuth = useCallback(
    async (credentials: SignInWithOAuthCredentials) => {
      if (user?.is_anonymous) return supabase.auth.linkIdentity(credentials);

      const response = await supabase.auth.signInWithOAuth(credentials);

      // If the user is a resident, update their onboarding state after successful OAuth sign-in
      const {
        data: { session: oauthSession },
      } = await supabase.auth.getSession();
      if (oauthSession?.user?.user_metadata.account_type === AccountType.resident) {
        await residentLoginMutation.mutateAsync();
      }

      return response;
    },
    [user, residentLoginMutation]
  );

  const signInAnonymously = useCallback(async () => supabase.auth.signInAnonymously(), []);

  const signUp = useCallback(
    async (credentials: SignUpWithPasswordCredentials): Promise<AuthResponse> => {
      if (user?.is_anonymous) {
        await supabase.auth.updateUser(credentials);
        return supabase.auth.signInWithPassword(credentials);
      }

      const response = await supabase.auth.signUp(credentials);

      // Allow users to continue without email verification
      // The SIGNED_IN event will be triggered automatically
      // PendingReferralHandler will process any pending referrals
      if (!response.error && response.data.user) {
        // Optional: Store email verification status for later use
        // You can show a banner or notification to encourage email verification
      }

      return response;
    },
    [user]
  );

  // Removed automatic redirect to verify email page
  // Users can now use the app without email verification
  useEffect(() => {
    if (user) {
      // Optional: You can add a notification here to remind users to verify their email
      // but don't redirect them
    }
  }, [user]);

  const signOut = useCallback(async () => {
    clearSupabaseSessionFromLocalStorage();
    setSession(null);
    queryClient.clear();
    await supabase.auth.signOut();
  }, [queryClient]);

  const resetPasswordForEmail = useCallback(
    async (
      email: string,
      captchaToken?: string,
      redirectTo?: string
    ): Promise<{ error: Error | null }> => {
      const options: { redirectTo: string; captchaToken?: string } = {
        redirectTo: redirectTo || `${window.location.origin}${PublicRoutes.resetPassword}`,
      };

      if (captchaToken) {
        options.captchaToken = captchaToken;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email, options);

      return { error };
    },
    []
  );

  const checkAndHandleReferral = useCallback(
    async (): Promise<string | null> =>
      // This function will be implemented by components that handle referrals
      // For now, return null to maintain backward compatibility
      null,
    []
  );

  const value = useMemo(
    () => ({
      refreshUser,
      signInWithOAuth,
      signInWithPassword,
      signInAnonymously,
      signUp,
      signOut,
      resetPasswordForEmail,
      shouldHaveAuthToken,
      user,
      session,
      isAuthenticated,
      isPropertyManager,
      isResident,
      checkAndHandleReferral,
      isAuthLoading,
    }),
    [
      refreshUser,
      signInWithOAuth,
      signInWithPassword,
      signInAnonymously,
      signUp,
      signOut,
      resetPasswordForEmail,
      shouldHaveAuthToken,
      user,
      session,
      isAuthenticated,
      isPropertyManager,
      isResident,
      checkAndHandleReferral,
      isAuthLoading,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthProvider;
